import { useMemo, useState } from "react";
import { parseEther } from "ethers";
import { Loader2, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { TokenLogo } from "@/components/common/TokenLogo";
import { usdtForFmi } from "@/lib/liquidity";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const toWei = (v) => { try { return v && Number(v) > 0 ? parseEther(String(v)) : 0n; } catch { return 0n; } };

export const Stat = ({ label, value, testId }) => (
  <div className="p-4 rounded-xl border border-[#2A2411] bg-[#050505]">
    <p className="text-xs text-zinc-500">{label}</p>
    <p data-testid={testId} className="font-display text-lg font-bold text-white mt-1">{value}</p>
  </div>
);

export const PoolStats = ({ pool }) => (
  <div data-testid="lp-pool-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    <Stat label="FMI in pool" value={fmt(pool?.reserves.FMI)} testId="lp-reserve-fmi" />
    <Stat label="USDT in pool" value={fmt(pool?.reserves.USDT)} testId="lp-reserve-usdt" />
    <Stat label="Pool price" value={pool?.price ? `$${fmt(pool.price, 4)}` : "—"} testId="lp-price" />
    <Stat label="Fee to providers" value={pool ? `${pool.feePercent}% per swap` : "—"} testId="lp-fee" />
  </div>
);

export const PositionCard = ({ position }) => (
  <div data-testid="lp-position" className="p-5 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5">
    <p className="text-sm font-medium text-[#D4AF37] mb-3">Your position</p>
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div><p className="text-xs text-zinc-500">Pool share</p><p data-testid="lp-position-share" className="font-mono text-white">{fmt(position?.sharePercent, 4)}%</p></div>
      <div><p className="text-xs text-zinc-500">FMI</p><p data-testid="lp-position-fmi" className="font-mono text-white">{fmt(position?.fmi, 4)}</p></div>
      <div><p className="text-xs text-zinc-500">USDT</p><p data-testid="lp-position-usdt" className="font-mono text-white">{fmt(position?.usdt, 2)}</p></div>
    </div>
  </div>
);

const SubmitBtn = ({ busy, step, disabled, onClick, testId, children }) => (
  <Button data-testid={testId} onClick={onClick} disabled={disabled || busy}
    className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-11">
    {busy ? <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />{step || "Working…"}</span> : children}
  </Button>
);

export const AddPanel = ({ pool, address, busy, step, onAdd }) => {
  const [fmi, setFmi] = useState("");
  const wei = toWei(fmi);
  const usdt = pool && wei ? Number(usdtForFmi(wei, pool)) / 1e6 : 0;
  return (
    <div data-testid="lp-add-panel" className="space-y-3">
      <div className="p-4 rounded-xl bg-[#050505] border border-[#2A2411]">
        <span className="text-xs text-zinc-500">FMI to deposit</span>
        <div className="flex items-center gap-3 mt-2">
          <Input data-testid="lp-add-fmi-input" type="number" min="0" placeholder="0.0" value={fmi} onChange={(e) => setFmi(e.target.value)}
            className="bg-transparent border-0 text-2xl font-display font-bold text-white p-0 focus-visible:ring-0" />
          <span className="flex items-center gap-1.5 text-white"><TokenLogo symbol="FMI" size="sm" />FMI</span>
        </div>
      </div>
      <div className="p-4 rounded-xl bg-[#050505] border border-[#2A2411] flex items-center justify-between">
        <span className="text-xs text-zinc-500">USDT needed (current ratio)</span>
        <span data-testid="lp-add-usdt-needed" className="font-mono text-white flex items-center gap-1.5">{fmt(usdt, 6)} <TokenLogo symbol="USDT" size="sm" /></span>
      </div>
      <SubmitBtn testId="lp-add-submit" busy={busy} step={step} disabled={!address || !wei} onClick={() => onAdd(wei, () => setFmi(""))}>
        <Plus size={16} className="mr-1" /> {address ? "Add liquidity" : "Connect wallet to add"}
      </SubmitBtn>
    </div>
  );
};

export const RemovePanel = ({ pool, position, busy, step, onRemove }) => {
  const [pct, setPct] = useState(100);
  const sliderValue = useMemo(() => [pct], [pct]);
  const owned = BigInt(position?.sharesRaw || "0");
  const shares = (owned * BigInt(pct)) / 100n;
  const part = pct / 100;
  return (
    <div data-testid="lp-remove-panel" className="space-y-4">
      <div className="flex items-center justify-between text-sm"><span className="text-zinc-400">Withdraw</span><span data-testid="lp-remove-percent" className="font-mono text-white">{pct}%</span></div>
      <Slider data-testid="lp-remove-slider" value={sliderValue} min={1} max={100} step={1} onValueChange={(v) => setPct(v[0])} />
      <p data-testid="lp-remove-preview" className="text-xs text-zinc-500">
        You receive about {fmt((position?.fmi || 0) * part, 4)} FMI + {fmt((position?.usdt || 0) * part, 2)} USDT
      </p>
      <SubmitBtn testId="lp-remove-submit" busy={busy} step={step} disabled={!pool || owned === 0n} onClick={() => onRemove(shares)}>
        <Minus size={16} className="mr-1" /> Withdraw liquidity
      </SubmitBtn>
    </div>
  );
};
